import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Send, 
  Loader2,
  MessageSquare,
  ArrowLeft
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Messages = () => {
  const { userId: selectedUserId } = useParams();
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    if (selectedUserId) {
      fetchMessages(selectedUserId);
      fetchUserInfo(selectedUserId);
    }
  }, [selectedUserId]);

  useEffect(() => {
    // Poll for new messages when in a conversation
    if (selectedUserId) {
      const interval = setInterval(() => fetchMessages(selectedUserId), 5000);
      return () => clearInterval(interval);
    }
  }, [selectedUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/conversations`, { withCredentials: true });
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/${userId}`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const fetchUserInfo = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      setSelectedUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUserId) return;

    setSending(true);
    try {
      const response = await axios.post(`${API_URL}/api/messages`, {
        receiver_id: selectedUserId,
        content: newMessage
      }, { withCredentials: true });
      
      setMessages([...messages, response.data]);
      setNewMessage('');
      fetchConversations(); // Refresh conversations list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden h-[calc(100vh-140px)]">
          <div className="flex h-full">
            {/* Conversations List */}
            <div className={`w-full md:w-80 border-r border-border flex flex-col ${selectedUserId ? 'hidden md:flex' : ''}`}>
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold">Messages</h2>
              </div>
              <ScrollArea className="flex-1">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-primary animate-spin" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No conversations yet</p>
                    <p className="text-sm mt-1">Connect with people to start messaging</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {conversations.map((conv) => (
                      <Link
                        key={conv.user.user_id}
                        to={`/messages/${conv.user.user_id}`}
                        className={`flex items-center gap-3 p-4 hover:bg-muted/50 transition-colors ${
                          selectedUserId === conv.user.user_id ? 'bg-secondary' : ''
                        }`}
                        data-testid={`conversation-${conv.user.user_id}`}
                      >
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={conv.user.picture} />
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {getInitials(conv.user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{conv.user.name}</p>
                          <p className="text-sm text-muted-foreground truncate">{conv.last_message}</p>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatDate(conv.last_time)}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            {/* Chat Area */}
            <div className={`flex-1 flex flex-col ${!selectedUserId ? 'hidden md:flex' : ''}`}>
              {selectedUserId && selectedUser ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-border flex items-center gap-3">
                    <Link to="/messages" className="md:hidden">
                      <Button variant="ghost" size="icon" className="rounded-full">
                        <ArrowLeft className="w-5 h-5" />
                      </Button>
                    </Link>
                    <Link to={`/profile/${selectedUser.user_id}`} className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={selectedUser.picture} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {getInitials(selectedUser.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold">{selectedUser.name}</p>
                        {selectedUser.headline && (
                          <p className="text-sm text-muted-foreground">{selectedUser.headline}</p>
                        )}
                      </div>
                    </Link>
                  </div>

                  {/* Messages */}
                  <ScrollArea className="flex-1 p-4">
                    <div className="space-y-4">
                      {messages.map((msg, index) => {
                        const isSent = msg.sender_id === user?.user_id;
                        const showDate = index === 0 || 
                          formatDate(messages[index - 1].created_at) !== formatDate(msg.created_at);
                        
                        return (
                          <React.Fragment key={msg.message_id}>
                            {showDate && (
                              <div className="text-center text-xs text-muted-foreground py-2">
                                {formatDate(msg.created_at)}
                              </div>
                            )}
                            <div className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}>
                              <div className={`message-bubble ${isSent ? 'message-sent' : 'message-received'}`}>
                                <p className="text-sm">{msg.content}</p>
                                <p className={`text-xs mt-1 ${isSent ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                  {formatTime(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>

                  {/* Message Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t border-border">
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="flex-1 rounded-full"
                        data-testid="message-input"
                      />
                      <Button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="rounded-full bg-primary hover:bg-primary/90"
                        data-testid="send-message-btn"
                      >
                        {sending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">Select a conversation</p>
                    <p className="text-sm">Choose from your existing conversations or start a new one</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Messages;
